import React from "react";
import { Dumbbell, Heart, Calendar, Check } from "lucide-react";
import { AnimatedSection } from "./AnimatedSection";

interface StudentFeaturesProps {
  visibleSections: Record<string, boolean>;
}

export const StudentFeatures: React.FC<StudentFeaturesProps> = ({
  visibleSections,
}) => {
  return (
    <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white dark:bg-black transition-colors duration-300">
      <div className="container mx-auto max-w-7xl">
        <AnimatedSection
          id="students-header"
          className="text-center mb-12 sm:mb-20"
          visible={visibleSections["students-header"] ?? true}
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 tracking-tight text-gray-900 dark:text-white">
            Para alunos
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 font-light">
            Experiência completa no app
          </p>
        </AnimatedSection>

        {/* Treinos Feature */}
        <AnimatedSection
          id="feature-1"
          className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center mb-20 sm:mb-32"
          visible={visibleSections["feature-1"] ?? true}
        >
          <div className="order-2 md:order-1">
            <div className="inline-block p-3 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl mb-4 sm:mb-6 transition-all duration-300 hover:scale-110 hover:rotate-6">
              <Dumbbell className="w-6 sm:w-8 h-6 sm:h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6 text-gray-900 dark:text-white">
              Treinos inteligentes
            </h3>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 leading-relaxed">
              Visualize seus treinos no estilo player do Spotify. Faça treinos
              pelo app, avalie, comente e tire dúvidas por chat em cada
              exercício.
            </p>
            <ul className="space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
              {[
                "Interface intuitiva tipo player musical",
                "Chat integrado em cada exercício",
                "Histórico completo de treinos",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 group">
                  <div className="bg-purple-100 dark:bg-purple-900/30 p-1 rounded-lg group-hover:scale-110 transition-transform">
                    <Check className="w-5 h-5 text-purple-600 dark:text-purple-500 flex-shrink-0" />
                  </div>
                  <span className="text-base sm:text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="order-1 md:order-2 relative group">
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl opacity-20 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl transform group-hover:scale-[1.02] transition-all duration-500">
              <img
                src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&h=1000&fit=crop"
                alt="Treinos"
                className="w-full h-auto"
              />
            </div>
          </div>
        </AnimatedSection>

        {/* Avaliação & Saúde Feature */}
        <AnimatedSection
          id="feature-2"
          className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center mb-20 sm:mb-32"
          visible={visibleSections["feature-2"] ?? true}
        >
          <div className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-r from-pink-600 to-purple-600 rounded-3xl opacity-20 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl transform group-hover:scale-[1.02] transition-all duration-500">
              <img
                src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&h=1000&fit=crop"
                alt="Avaliação e Saúde"
                className="w-full h-auto"
              />
            </div>
          </div>
          <div>
            <div className="inline-block p-3 bg-gradient-to-br from-pink-100 to-red-100 dark:from-pink-900/30 dark:to-red-900/30 rounded-2xl mb-4 sm:mb-6 transition-all duration-300 hover:scale-110 hover:rotate-6">
              <Heart className="w-6 sm:w-8 h-6 sm:h-8 text-pink-600 dark:text-pink-400" />
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6 text-gray-900 dark:text-white">
              Saúde 360°
            </h3>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 leading-relaxed">
              Acompanhamento completo com avaliação 360, contador de calorias
              por IA, fotos dos pratos e medidas corporais.
            </p>
            <ul className="space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
              {[
                "Avaliação de sono, estresse e motivação",
                "IA para contagem de calorias",
                "Acompanhamento alimentar com fotos",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 group">
                  <div className="bg-pink-100 dark:bg-pink-900/30 p-1 rounded-lg group-hover:scale-110 transition-transform">
                    <Check className="w-5 h-5 text-pink-600 dark:text-pink-500 flex-shrink-0" />
                  </div>
                  <span className="text-base sm:text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </AnimatedSection>

        {/* Agenda Feature */}
        <AnimatedSection
          id="feature-3"
          className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center"
          visible={visibleSections["feature-3"] ?? true}
        >
          <div className="order-2 md:order-1">
            <div className="inline-block p-3 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl mb-4 sm:mb-6 transition-all duration-300 hover:scale-110 hover:rotate-6">
              <Calendar className="w-6 sm:w-8 h-6 sm:h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6 text-gray-900 dark:text-white">
              Agenda simplificada
            </h3>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 leading-relaxed">
              Agende suas aulas e visualize todos os seus compromissos em um só
              lugar.
            </p>
            <ul className="space-y-3 sm:space-y-4 text-gray-700 dark:text-gray-300">
              {[
                "Agendamento fácil e rápido",
                "Visualização de todos os agendamentos",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 group">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-1 rounded-lg group-hover:scale-110 transition-transform">
                    <Check className="w-5 h-5 text-blue-600 dark:text-blue-500 flex-shrink-0" />
                  </div>
                  <span className="text-base sm:text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="order-1 md:order-2 relative group">
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl opacity-20 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl transform group-hover:scale-[1.02] transition-all duration-500">
              <img
                src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=1000&fit=crop"
                alt="Agenda"
                className="w-full h-auto"
              />
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};
